import React, { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AqPickers.scss";

function pad2(value) {
  return String(value).padStart(2, "0");
}

function digitsOnly(raw, maxLen) {
  return String(raw || "")
    .replace(/\D/g, "")
    .slice(0, maxLen);
}

/** Allow typing day 1–31 only (partial input OK). */
function sanitizeDayInput(raw) {
  let d = digitsOnly(raw, 2);
  if (!d) return "";
  if (d.length === 1) {
    const n = Number(d);
    if (n > 3) return String(n); // 4–9 ok as single day digit
    return d;
  }
  let n = Number(d);
  if (n < 1) return "01";
  if (n > 31) return "31";
  return d;
}

/** Allow typing month 1–12 only. */
function sanitizeMonthInput(raw) {
  let m = digitsOnly(raw, 2);
  if (!m) return "";
  if (m.length === 1) {
    const n = Number(m);
    if (n > 1) return String(n); // 2–9 ok
    return m;
  }
  let n = Number(m);
  if (n < 1) return "01";
  if (n > 12) return "12";
  return m;
}

/** Year: digits only, max 4; block absurd leading values while typing. */
function sanitizeYearInput(raw) {
  let y = digitsOnly(raw, 4);
  if (!y) return "";
  if (y.length === 1 && Number(y) === 0) return "";
  return y;
}

function daysInMonth(year, month) {
  if (!year || !month || month < 1 || month > 12) return 31;
  return new Date(year, month, 0).getDate();
}

function partsFromDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return { day: "", month: "", year: "" };
  }
  return {
    day: pad2(date.getDate()),
    month: pad2(date.getMonth() + 1),
    year: String(date.getFullYear()),
  };
}

function dateFromParts(dayRaw, monthRaw, yearRaw) {
  const day = Number(dayRaw);
  const month = Number(monthRaw);
  const year = Number(yearRaw);
  if (
    !Number.isFinite(day) ||
    !Number.isFinite(month) ||
    !Number.isFinite(year) ||
    day < 1 ||
    day > 31 ||
    month < 1 ||
    month > 12 ||
    year < 1900 ||
    year > 2100
  ) {
    return null;
  }
  const maxDay = daysInMonth(year, month);
  if (day > maxDay) return null;
  const d = new Date(year, month - 1, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

const SegmentDateTrigger = forwardRef(function SegmentDateTrigger(
  {
    value,
    onClick,
    disabled,
    invalid,
    open = false,
    day,
    month,
    year,
    onDayChange,
    onMonthChange,
    onYearChange,
    onCommitParts,
    "aria-label": ariaLabel,
  },
  ref,
) {
  return (
    <div
      ref={ref}
      className={`aq-date-segments${open ? " is-open" : ""}${
        invalid ? " is-invalid" : ""
      }${disabled ? " is-disabled" : ""}`}
    >
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className="aq-date-segments__part aq-date-segments__part--day"
        placeholder="DD"
        maxLength={2}
        disabled={disabled}
        value={day}
        aria-label="Day"
        onChange={(e) => onDayChange(e.target.value)}
        onBlur={() => onCommitParts()}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="aq-date-segments__sep">/</span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className="aq-date-segments__part aq-date-segments__part--month"
        placeholder="MM"
        maxLength={2}
        disabled={disabled}
        value={month}
        aria-label="Month"
        onChange={(e) => onMonthChange(e.target.value)}
        onBlur={() => onCommitParts()}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="aq-date-segments__sep">/</span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className="aq-date-segments__part aq-date-segments__part--year"
        placeholder="YYYY"
        maxLength={4}
        disabled={disabled}
        value={year}
        aria-label="Year"
        onChange={(e) => onYearChange(e.target.value)}
        onBlur={() => onCommitParts()}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        className="aq-date-segments__calendar"
        disabled={disabled}
        aria-label={ariaLabel || "Open date picker"}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          onClick?.(e);
        }}
      >
        <i className="far fa-calendar" aria-hidden="true" />
      </button>
      <span className="visually-hidden">{value || ""}</span>
    </div>
  );
});

/**
 * AQ segmented date field (DD / MM / YYYY + calendar) with styled popup calendar.
 */
export default function SegmentDatePicker({
  selected = null,
  onChange,
  disabled = false,
  invalid = false,
  minDate = null,
  maxDate = null,
  isClearable = false,
  placeholder = "Select date",
  "aria-label": ariaLabel = "Select date",
}) {
  const initial = partsFromDate(selected);
  const [day, setDay] = useState(initial.day);
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const next = partsFromDate(selected);
    setDay(next.day);
    setMonth(next.month);
    setYear(next.year);
  }, [selected]);

  const revertToSelected = () => {
    const next = partsFromDate(selected);
    setDay(next.day);
    setMonth(next.month);
    setYear(next.year);
  };

  const commitParts = () => {
    if (!day && !month && !year) {
      if (isClearable) onChange?.(null);
      else revertToSelected();
      return;
    }

    // Incomplete — revert to last valid selected date
    if (day.length < 1 || month.length < 1 || year.length < 4) {
      revertToSelected();
      return;
    }

    let dNum = Number(day);
    let mNum = Number(month);
    let yNum = Number(year);

    if (yNum < 1900 || yNum > 2100) {
      revertToSelected();
      return;
    }
    if (mNum < 1 || mNum > 12) {
      revertToSelected();
      return;
    }

    const maxDay = daysInMonth(yNum, mNum);
    if (dNum < 1 || dNum > maxDay) {
      revertToSelected();
      return;
    }

    const parsed = dateFromParts(pad2(dNum), pad2(mNum), String(yNum));
    if (!parsed) {
      revertToSelected();
      return;
    }
    if (minDate && parsed < startOfDay(minDate)) {
      revertToSelected();
      return;
    }
    if (maxDate && parsed > startOfDay(maxDate)) {
      revertToSelected();
      return;
    }

    setDay(pad2(dNum));
    setMonth(pad2(mNum));
    setYear(String(yNum));
    onChange?.(parsed);
  };

  return (
    <div className="aq-picker-wrap aq-picker-wrap--date">
      <DatePicker
        selected={selected}
        onChange={(date) => onChange?.(date)}
        onCalendarOpen={() => setCalendarOpen(true)}
        onCalendarClose={() => setCalendarOpen(false)}
        disabled={disabled}
        minDate={minDate || undefined}
        maxDate={maxDate || undefined}
        isClearable={false}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        dateFormat="dd/MM/yyyy"
        placeholderText={placeholder}
        calendarClassName="aq-datepicker-calendar"
        popperClassName="aq-datepicker-popper"
        popperProps={{ strategy: "fixed" }}
        shouldCloseOnSelect
        customInput={
          <SegmentDateTrigger
            disabled={disabled}
            invalid={invalid}
            open={calendarOpen}
            day={day}
            month={month}
            year={year}
            onDayChange={(raw) => setDay(sanitizeDayInput(raw))}
            onMonthChange={(raw) => setMonth(sanitizeMonthInput(raw))}
            onYearChange={(raw) => setYear(sanitizeYearInput(raw))}
            onCommitParts={commitParts}
            aria-label={ariaLabel}
          />
        }
      />
    </div>
  );
}
